/**
 * Social member-visible action chrome (UX-05-A / UX-05-B).
 * Does not change follow / share / comment domain behavior.
 */

export const SOCIAL_ACTION_PRODUCT_CHROME = Object.freeze({
  follow: '追蹤',
  following: '已追蹤',
  followAria: '追蹤作者',
  unfollowAria: '取消追蹤作者',
  likeAria: '按讚',
  unlikeAria: '取消按讚',
  saveAria: '收藏',
  unsaveAria: '取消收藏',
  openPostAria: (authorName) => `開啟 ${authorName || '旅人'} 的貼文`,
  readCommentsAria: (count) => `查看 ${Number(count) || 0} 則留言`,
  commentsCountAria: (count) => `${Number(count) || 0} 則留言`,
  comments: '留言',
  addComment: '新增留言',
  postComment: '發佈留言',
  posting: '發佈中…',
  share: '分享',
  shareAria: '分享貼文',
  quote: '引用',
  quoteAria: '引用此貼文',
  repost: '轉發',
  repostUnavailable: '尚未開放',
  repostMenuAria: '轉發或引用選單',
  moreMenuAria: '更多操作',
  quotePreviewAria: '引用來源預覽',
  quoteUnavailableAria: '引用來源不可用',
  quoteUnavailableTitle: '引用來源不可用',
  quoteUnavailableBody: '此來源可能受限制、非公開，或目前無法引用。',
  quoteFromLabel: (authorName) => `引用自 ${authorName || '旅人'}`,
  quoteEmptyPreview: '查看引用來源',
  removeQuote: '移除引用',
  removeQuoteAria: '移除引用來源',
  quoteLoading: '載入引用來源…',
  viewReplies: '查看回覆',
  noComments: '還沒有留言，來開個話題吧。',
  commentPlaceholder: '寫下你的想法…',
  signInToComment: '登入後留言',
  loadingComments: '載入留言…',
  seekingComment: '正在尋找留言…',
  commentLocated: '已定位到這則留言',
  commentContextMissing: '找不到該則留言',
  tryAgain: '再試一次',
  keepExploring: '繼續探索',
  keepExploringHint: '前往旅人頁面，或查看對方分享的公開行程，發現更多旅遊靈感。',
  backToExplore: '返回探索',
  backToFollowing: '回到追蹤中',
  loadingStory: '載入貼文中…',
  postUnavailableTitle: '貼文無法使用',
  postUnavailableBody: '這則貼文可能不存在，或目前無法瀏覽。',
  loadStoryFailedTitle: '無法載入這則貼文',
  coverAlt: (authorName) => `${authorName || '旅人'} 的旅遊故事`,
  visibility: '可見範圍',
  visibilityLabel: '可見範圍：',
  unpublish: '下架貼文',
  unpublishing: '下架中…',
  republish: '重新發佈',
  republishing: '發佈中…',
  delete: '刪除貼文',
  deleting: '刪除中…',
  confirmDelete: '確認刪除貼文',
  deletePrompt: '確定要刪除這則貼文嗎？刪除後無法復原。',
  cancel: '取消',
});

/** Social compose product chrome (UX-05-B／Epic D2C Batch 5). Does not translate UGC. */
export const SOCIAL_COMPOSE_PRODUCT_CHROME = Object.freeze({
  eyebrow: '獨立貼文',
  title: '新貼文',
  helper: '建立一則社群貼文。這不是回憶分享，也不是公開行程發佈。',
  textPlaceholder: '分享這趟旅程的故事…',
  closeAria: '關閉發文',
  moreMenuAria: '更多操作',
  backToExplore: '返回探索',
  cancel: '取消',
  publish: '發布',
  publishing: '發布中…',
  postOptions: '貼文選項',
  postOptionsTitle: '貼文選項',
  postOptionsDone: '完成',
  locationLegend: '地點（選填）',
  locationHint: '選擇具名地點。不會使用地圖座標。',
  coverLabel: '封面圖片（選填）',
  coverPreviewAlt: '封面預覽',
  coverBadge: '封面',
  removeCover: '移除封面',
  addImage: '+ 新增圖片',
  addImageShort: '圖片',
  reorderHint: '使用左右移動調整順序',
  mediaCount: (count, max) => `已選擇 ${count}/${max} 張 · 可繼續新增、排序或移除`,
  mediaLegend: '圖片（最多 9 張）',
  uploadingCover: '上傳封面中…',
  coverReady: '封面已就緒。重試時會沿用這張圖片。',
  coverUploadFailed: '封面上傳失敗，請換一張圖片再試。',
  publishFailed: '無法發布這則貼文，請稍後再試。',
  toolGif: 'GIF',
  toolSticker: '貼圖',
  toolMusic: '音樂',
  toolMore: '更多工具',
  gapUnavailable: '尚未開放',
  scheduleGap: '排定發布',
  aiTagGap: '新增 AI 標籤',
  validation: Object.freeze({
    postKind: '請選擇支援的貼文類型。',
    visibility: '請選擇可見範圍。',
    replyPolicy: '請選擇誰可以回覆。',
    quotePolicy: '請選擇是否允許引用。',
    textTooLong: (max) => `內容最多 ${max} 字。`,
    moodNeedsText: '心情貼文需要填寫內容。',
    foodNeedsTextOrLocation: '美食貼文需要內容或具名地點。',
    checkInNeedsLocation: '打卡需要選擇地點。',
    coverKeyInvalid: '封面必須是 travel/image 物件鍵。',
  }),
});

/** 打卡 compose 產品文案（PD-CHK-01）。 */
export const SOCIAL_CHECKIN_PRODUCT_CHROME = Object.freeze({
  eyebrow: '打卡',
  title: '留下打卡',
  helper: '選擇地點，分享你現在的足跡。這不是住宿入住，也不是美食貼文。',
  backToExplore: '返回探索',
  cancel: '取消',
  publish: '打卡',
  publishing: '送出中…',
  locationLegend: '地點（必填）',
  locationHint: '請選擇具名地點。',
  publishFailed: '無法完成打卡，請稍後再試。',
});

/** 發文地點搜尋（沿用公開地點建議；僅回傳可公開使用的具名地點）。 */
export const SOCIAL_LOCATION_SEARCH_CHROME = Object.freeze({
  label: '搜尋地點',
  placeholder: '輸入地點名稱',
  hint: '至少輸入 2 個字。',
  searching: '搜尋中…',
  empty: '找不到符合的地點，請換個關鍵字。',
  resultCount: (count) => `找到 ${count} 個地點。`,
  failed: '暫時無法搜尋地點，請稍後再試。',
  resolveFailed: '這個地點目前無法使用，請選擇其他地點。',
  use: '使用此地點',
  selected: '已選擇',
});
